import React, { useState, useEffect } from "react";
import { FiSliders, FiMail, FiEye, FiEyeOff, FiSave, FiSend } from "react-icons/fi";
import { toast } from "react-toastify";
import { getSmtpConfig, saveSmtpConfig, sendSmtpTestEmail } from "../services/settingsService";

const Settings = () => {
  const [activeTab, setActiveTab] = useState("general");
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [smtpForm, setSmtpForm] = useState({
    host: "",
    port: "",
    user: "",
    password: "",
    from: "",
    secure: false,
  });
  const [testEmailTo, setTestEmailTo] = useState("");

  useEffect(() => {
    if (activeTab === "smtp") {
      loadSmtpConfig();
    }
  }, [activeTab]);

  const loadSmtpConfig = async () => {
    setSmtpLoading(true);
    try {
      const data = await getSmtpConfig();
      if (data && typeof data === "object") {
        setSmtpForm((prev) => ({
          ...prev,
          host: data.host ?? "",
          port: data.port != null ? String(data.port) : "",
          user: data.user ?? "",
          password: data.password === "********" ? "" : (data.password ?? ""),
          from: data.from ?? "",
          secure: Boolean(data.secure),
        }));
      }
    } catch (err) {
      // Endpoint may not exist yet; leave form empty
      if (err?.status !== 404) {
        toast.error(err?.message || "Failed to load SMTP config");
      }
    } finally {
      setSmtpLoading(false);
    }
  };

  const handleSmtpChange = (field, value) => {
    setSmtpForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    setSmtpSaving(true);
    try {
      await saveSmtpConfig({
        host: smtpForm.host.trim(),
        port: smtpForm.port ? Number(smtpForm.port) : undefined,
        user: smtpForm.user.trim() || undefined,
        password: smtpForm.password || undefined,
        from: smtpForm.from.trim() || undefined,
        secure: smtpForm.secure,
      });
      toast.success("SMTP settings saved.");
    } catch (err) {
      toast.error(err?.message || "Failed to save SMTP settings");
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    const email = testEmailTo.trim();
    if (!email) {
      toast.error("Enter an email address to send the test to.");
      return;
    }
    setTestEmailSending(true);
    try {
      await sendSmtpTestEmail(email);
      toast.success("Test email sent. Check the inbox.");
    } catch (err) {
      toast.error(err?.message || "Failed to send test email");
    } finally {
      setTestEmailSending(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
            <FiSliders className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600 text-sm">Admin-only application settings</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab("general")}
              className={`
                flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors
                ${activeTab === "general"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              General
            </button>
            <button
              onClick={() => setActiveTab("smtp")}
              className={`
                flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors
                ${activeTab === "smtp"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              <FiMail size={16} className="mr-2" />
              SMTP
            </button>
          </nav>
        </div>

        {/* General tab */}
        {activeTab === "general" && (
          <div className="border-t border-gray-200 pt-6">
            {/* <p className="text-gray-500">
              Configure application preferences and system options here. This page is only visible to users with admin role.
            </p>
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600">
                Add your settings sections and options below as needed.
              </p>
            </div> */}
          </div>
        )}

        {/* SMTP tab */}
        {activeTab === "smtp" && (
          <div className="border-t border-gray-200 pt-6">
            <p className="text-gray-500 mb-6">
              Configure SMTP for sending emails. Save your settings and use &quot;Send test email&quot; to verify.
            </p>
            {smtpLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
              </div>
            ) : (
              <form onSubmit={handleSaveSmtp} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Host</label>
                    <input
                      type="text"
                      value={smtpForm.host}
                      onChange={(e) => handleSmtpChange("host", e.target.value)}
                      placeholder="smtp.example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={smtpForm.port}
                      onChange={(e) => handleSmtpChange("port", e.target.value)}
                      placeholder="587"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    type="text"
                    value={smtpForm.user}
                    onChange={(e) => handleSmtpChange("user", e.target.value)}
                    placeholder="SMTP username"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={smtpForm.password}
                      onChange={(e) => handleSmtpChange("password", e.target.value)}
                      placeholder="Leave blank to keep existing"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-700"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Leave blank when loading/saving if you don&apos;t want to change the stored password.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From address</label>
                  <input
                    type="email"
                    value={smtpForm.from}
                    onChange={(e) => handleSmtpChange("from", e.target.value)}
                    placeholder="noreply@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    id="smtp-secure"
                    type="checkbox"
                    checked={smtpForm.secure}
                    onChange={(e) => handleSmtpChange("secure", e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="smtp-secure" className="ml-2 text-sm text-gray-700">
                    Use TLS/SSL (secure connection)
                  </label>
                </div>
                <div className="flex flex-wrap gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={smtpSaving}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiSave size={18} className="mr-2" />
                    {smtpSaving ? "Saving..." : "Save SMTP settings"}
                  </button>
                </div>

                <div className="pt-6 mt-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Send test email</h3>
                  <p className="text-sm text-gray-500 mb-3">
                    Verify your SMTP configuration by sending a test email to an address of your choice.
                  </p>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">To email</label>
                      <input
                        type="email"
                        value={testEmailTo}
                        onChange={(e) => setTestEmailTo(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleSendTestEmail}
                      disabled={testEmailSending}
                      className="inline-flex items-center px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiSend size={18} className="mr-2" />
                      {testEmailSending ? "Sending..." : "Send test email"}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
